 
'use client';

import React, { useState, useMemo } from 'react';
import dynamic from 'next/dynamic';
import { ExtendedArchiMateDiagram } from '@/lib/archimate/extended-types';
import {
  createSequenceDiagram,
  createParticipantFromElement,
  createParticipant,
  createMessage,
  createReturnMessage,
  createNote,
  createFragment,
  generateMermaid,
  validateSequenceDiagram,
  exportSequenceDiagram,
  type SequenceDiagram,
  type Participant,
  type Message,
  type Note,
  type MessageType,
  type ParticipantType,
} from '@/lib/archimate/sequence-diagram-utils';
import {
  X,
  Users,
  ArrowRight,
  FileText,
  Download,
  Plus,
  Trash2,
  Edit3,
  Code,
  Eye,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';
import { PanelHelpDialog, HelpIconButton } from './PanelHelpDialog';

// Lazy load Mermaid renderer (large dependency)
const MermaidRenderer = dynamic(() => import('./MermaidRenderer'), {
  ssr: false,
  loading: () => <div className="flex items-center justify-center p-8 text-foreground-tertiary">Loading diagram...</div>,
});

interface SequenceDiagramPanelProps {
  diagram: ExtendedArchiMateDiagram;
  onClose: () => void;
}

export default function SequenceDiagramPanel({ diagram, onClose }: SequenceDiagramPanelProps) {
  const [showPreview, setShowPreview] = useState(false);
  const [sequenceDiagram, setSequenceDiagram] = useState<SequenceDiagram>(() =>
    createSequenceDiagram('New Sequence Diagram', diagram.id)
  );
  const [selectedParticipantIds, setSelectedParticipantIds] = useState<string[]>([]);
  const [showHelp, setShowHelp] = useState(false);

  // Generate Mermaid code
  const mermaidCode = useMemo(() => {
    try {
      return generateMermaid(sequenceDiagram);
    } catch (error) {
      console.error('Failed to generate Mermaid:', error);
      return '';
    }
  }, [sequenceDiagram]);

  const validation = useMemo(() => validateSequenceDiagram(sequenceDiagram), [sequenceDiagram]);

  const handleAddParticipantsFromElements = () => {
    const newParticipants = selectedParticipantIds.map((elementId, index) => {
      const element = diagram.elements.find((e) => e.id === elementId);
      if (!element) return null;
      return createParticipantFromElement(element, sequenceDiagram.participants.length + index);
    }).filter(Boolean) as Participant[];

    setSequenceDiagram({
      ...sequenceDiagram,
      participants: [...sequenceDiagram.participants, ...newParticipants],
      updatedAt: Date.now(),
    });

    setSelectedParticipantIds([]);
  };

  const handleAddParticipant = () => {
    const name = prompt('Enter participant name:');
    if (!name) return;

    const participant = createParticipant(name, 'component', sequenceDiagram.participants.length);
    setSequenceDiagram({
      ...sequenceDiagram,
      participants: [...sequenceDiagram.participants, participant],
      updatedAt: Date.now(),
    });
  };

  const handleDeleteParticipant = (participantId: string) => {
    setSequenceDiagram({
      ...sequenceDiagram,
      participants: sequenceDiagram.participants.filter((p) => p.id !== participantId),
      messages: sequenceDiagram.messages.filter(
        (m) => m.from !== participantId && m.to !== participantId
      ),
      updatedAt: Date.now(),
    });
  };

  const handleAddMessage = () => {
    if (sequenceDiagram.participants.length < 2) {
      alert('Add at least 2 participants first');
      return;
    }

    const from = sequenceDiagram.participants[0].id;
    const to = sequenceDiagram.participants[1].id;
    const label = 'New Message';
    const sequence = sequenceDiagram.messages.length;

    const message = createMessage(from, to, label, 'sync', sequence);
    setSequenceDiagram({
      ...sequenceDiagram,
      messages: [...sequenceDiagram.messages, message],
      updatedAt: Date.now(),
    });
  };

  const handleDeleteMessage = (messageId: string) => {
    setSequenceDiagram({
      ...sequenceDiagram,
      messages: sequenceDiagram.messages.filter((m) => m.id !== messageId),
      updatedAt: Date.now(),
    });
  };

  const handleUpdateMessage = (messageId: string, updates: Partial<Message>) => {
    setSequenceDiagram({
      ...sequenceDiagram,
      messages: sequenceDiagram.messages.map((m) =>
        m.id === messageId ? { ...m, ...updates } : m
      ),
      updatedAt: Date.now(),
    });
  };

  const handleExportMermaid = () => {
    const blob = new Blob([mermaidCode], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${sequenceDiagram.name}.mmd`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportJSON = () => {
    const exported = exportSequenceDiagram(sequenceDiagram);
    const json = JSON.stringify(exported, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${sequenceDiagram.name}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
    <div className="fixed top-0 right-0 h-full w-[700px] bg-background/95 backdrop-blur border-l border-border shadow-2xl z-50 flex flex-col animate-in slide-in-from-right duration-300">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-border">
        <div className="flex items-center gap-3">
          <ArrowRight className="w-6 h-6 text-accent" />
          <h2 className="text-xl font-bold text-foreground">Sequence Diagram Editor</h2>
          <HelpIconButton onClick={() => setShowHelp(true)} />
        </div>
        <button
          onClick={onClose}
          className="p-2 hover:bg-cream/10 rounded-lg transition-colors"
          aria-label="Close panel"
        >
          <X className="w-5 h-5 text-foreground" />
        </button>
      </div>

      {/* Help Dialog */}
      {showHelp && (
        <PanelHelpDialog
          panelKey="sequence"
          onClose={() => setShowHelp(false)}
        />
      )}

      {/* Preview Button */}
      <div className="px-6 py-4 border-b border-border">
        <button
          onClick={() => setShowPreview(true)}
          className="flex items-center gap-2 px-4 py-2 bg-accent hover:bg-accent-hover text-accent-foreground rounded-lg font-medium transition-all"
        >
          <Eye className="w-4 h-4" />
          Preview Diagram
        </button>
      </div>

      {/* Validation Status */}
      {!validation.valid && (
        <div className="mx-6 mt-4 bg-red-500/10 border border-red-500/20 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="w-4 h-4 text-red-400" />
            <span className="text-sm font-medium text-red-400">Validation Errors</span>
          </div>
          <div className="space-y-1">
            {validation.errors.map((error, idx) => (
              <div key={idx} className="text-xs text-foreground-secondary">
                • {error}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Editor Content */}
      <div className="flex-1 overflow-auto">
          {/* Participants Section */}
          <div className="px-6 py-4 border-b border-border">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-foreground-secondary">Participants</h3>
              <button
                onClick={handleAddParticipant}
                className="flex items-center gap-1 px-3 py-1.5 bg-accent-muted text-accent rounded-lg text-xs font-medium hover:bg-accent/30 transition-colors"
              >
                <Plus className="w-3 h-3" />
                Add
              </button>
            </div>

            {/* Add from ArchiMate Elements */}
            <div className="mb-4">
              <label className="block text-xs text-foreground-tertiary mb-2">Add from Diagram Elements</label>
              <div className="flex gap-2">
                <select
                  multiple
                  value={selectedParticipantIds}
                  onChange={(e) =>
                    setSelectedParticipantIds(
                      Array.from(e.target.selectedOptions, (option) => option.value)
                    )
                  }
                  className="flex-1 px-3 py-2 bg-cream/5 border border-border rounded-lg text-sm text-foreground focus:outline-none focus:border-accent"
                  size={3}
                >
                  {diagram.elements
                    .filter(
                      (e) =>
                        !sequenceDiagram.participants.some((p) => p.elementId === e.id)
                    )
                    .map((element) => (
                      <option key={element.id} value={element.id}>
                        {element.name} ({element.type})
                      </option>
                    ))}
                </select>
                <button
                  onClick={handleAddParticipantsFromElements}
                  disabled={selectedParticipantIds.length === 0}
                  className="px-3 py-2 bg-cream/10 text-foreground rounded-lg text-sm hover:bg-cream/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Add →
                </button>
              </div>
            </div>

            {/* Participant List */}
            <div className="space-y-2">
              {sequenceDiagram.participants.length === 0 && (
                <div className="text-sm text-foreground-tertiary italic text-center py-4">
                  No participants yet. Add some to start building your sequence diagram.
                </div>
              )}
              {sequenceDiagram.participants.map((participant) => (
                <div
                  key={participant.id}
                  className="flex items-center justify-between bg-cream/5 rounded-lg p-3"
                >
                  <div>
                    <div className="font-medium text-foreground">{participant.name}</div>
                    <div className="text-xs text-foreground-tertiary">
                      {participant.type} • {participant.alias}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeleteParticipant(participant.id)}
                    className="p-2 hover:bg-red-500/20 text-red-400 rounded-lg transition-colors"
                    aria-label="Delete participant"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Messages Section */}
          <div className="px-6 py-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-foreground-secondary">Messages</h3>
              <button
                onClick={handleAddMessage}
                className="flex items-center gap-1 px-3 py-1.5 bg-accent-muted text-accent rounded-lg text-xs font-medium hover:bg-accent/30 transition-colors"
              >
                <Plus className="w-3 h-3" />
                Add
              </button>
            </div>

            <div className="space-y-2">
              {sequenceDiagram.messages.length === 0 && (
                <div className="text-sm text-foreground-tertiary italic text-center py-4">
                  No messages yet. Add messages to show interactions.
                </div>
              )}
              {sequenceDiagram.messages
                .sort((a, b) => a.sequence - b.sequence)
                .map((message, index) => (
                  <MessageEditor
                    key={message.id}
                    message={message}
                    participants={sequenceDiagram.participants}
                    onUpdate={(updates) => handleUpdateMessage(message.id, updates)}
                    onDelete={() => handleDeleteMessage(message.id)}
                  />
                ))}
            </div>
          </div>
        </div>

      {/* Export Actions */}
      <div className="px-6 py-4 border-t border-border space-y-2">
        <button
          onClick={handleExportMermaid}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-accent hover:bg-accent-hover text-accent-foreground rounded-lg font-medium transition-colors"
        >
          <FileText className="w-4 h-4" />
          Export Mermaid
        </button>

        <button
          onClick={handleExportJSON}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-cream/10 hover:bg-cream/20 text-foreground rounded-lg font-medium transition-colors"
        >
          <Download className="w-4 h-4" />
          Export JSON
        </button>
      </div>
    </div>

      {/* Preview Overlay */}
      {showPreview && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-8"
          onClick={() => setShowPreview(false)}
        >
          <div
            className="bg-background border border-border rounded-lg shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Overlay Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-border sticky top-0 bg-background z-10">
              <div className="flex items-center gap-3">
                <Eye className="w-6 h-6 text-accent" />
                <h3 className="text-xl font-bold text-foreground">Diagram Preview</h3>
              </div>
              <button
                onClick={() => setShowPreview(false)}
                className="p-2 hover:bg-cream/10 rounded-lg transition-colors"
                aria-label="Close preview"
              >
                <X className="w-5 h-5 text-foreground" />
              </button>
            </div>

            {/* Mermaid Diagram */}
            <div className="p-8 bg-background min-h-[400px]">
              <MermaidRenderer chart={mermaidCode} className="w-full h-full" />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ============================================================================
// MESSAGE EDITOR COMPONENT
// ============================================================================

interface MessageEditorProps {
  message: Message;
  participants: Participant[];
  onUpdate: (updates: Partial<Message>) => void;
  onDelete: () => void;
}

function MessageEditor({ message, participants, onUpdate, onDelete }: MessageEditorProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const fromParticipant = participants.find((p) => p.id === message.from);
  const toParticipant = participants.find((p) => p.id === message.to);

  const messageTypeColors: Record<MessageType, string> = {
    sync: 'bg-blue-500/20 text-blue-400',
    async: 'bg-purple-500/20 text-purple-400',
    return: 'bg-green-500/20 text-green-400',
    create: 'bg-yellow-500/20 text-yellow-400',
    destroy: 'bg-red-500/20 text-red-400',
  };

  return (
    <div className="bg-cream/5 rounded-lg p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 flex-1">
          <span className="text-sm text-foreground-secondary">{fromParticipant?.name || '?'}</span>
          <ArrowRight className="w-3 h-3 text-foreground-tertiary" />
          <span className="text-sm text-foreground-secondary">{toParticipant?.name || '?'}</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1 hover:bg-cream/10 rounded transition-colors"
            aria-label="Edit message"
          >
            <Edit3 className="w-3 h-3 text-foreground-tertiary" />
          </button>
          <button
            onClick={onDelete}
            className="p-1 hover:bg-red-500/20 text-red-400 rounded transition-colors"
            aria-label="Delete message"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      </div>

      <div className="text-sm font-medium text-foreground mb-1">{message.label}</div>

      <div className="flex items-center gap-2">
        <span
          className={`text-xs px-2 py-0.5 rounded ${
            messageTypeColors[message.type]
          }`}
        >
          {message.type}
        </span>
        <span className="text-xs text-foreground-tertiary">#{message.sequence}</span>
      </div>

      {isExpanded && (
        <div className="mt-3 space-y-2 pt-3 border-t border-border-muted">
          <div>
            <label className="block text-xs text-foreground-tertiary mb-1">Label</label>
            <input
              type="text"
              value={message.label}
              onChange={(e) => onUpdate({ label: e.target.value })}
              className="w-full px-3 py-2 bg-cream/5 border border-border rounded text-sm text-foreground focus:outline-none focus:border-accent"
            />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs text-foreground-tertiary mb-1">From</label>
              <select
                value={message.from}
                onChange={(e) => onUpdate({ from: e.target.value })}
                className="w-full px-3 py-2 bg-cream/5 border border-border rounded text-sm text-foreground focus:outline-none focus:border-accent"
              >
                {participants.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs text-foreground-tertiary mb-1">To</label>
              <select
                value={message.to}
                onChange={(e) => onUpdate({ to: e.target.value })}
                className="w-full px-3 py-2 bg-cream/5 border border-border rounded text-sm text-foreground focus:outline-none focus:border-accent"
              >
                {participants.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs text-foreground-tertiary mb-1">Type</label>
            <select
              value={message.type}
              onChange={(e) => onUpdate({ type: e.target.value as MessageType })}
              className="w-full px-3 py-2 bg-cream/5 border border-border rounded text-sm text-foreground focus:outline-none focus:border-accent"
            >
              <option value="sync">Synchronous</option>
              <option value="async">Asynchronous</option>
              <option value="return">Return</option>
              <option value="create">Create</option>
              <option value="destroy">Destroy</option>
            </select>
          </div>
        </div>
      )}
    </div>
  );
}
